// Pure (network-free) helpers + constants for the Teams/Standings/Stats UX.
// These are unit-tested and shared by pages and the API layer.

import { coverageIsProcessing, readRetryAfter } from "./playerPolling";

export const NFL_CONFERENCES = ["AFC", "NFC"];
export const NFL_DIVISIONS = ["EAST", "NORTH", "SOUTH", "WEST"];

// Season selector options are UI config (NOT fabricated sports data). When the
// backend exposes a /seasons endpoint, source these from it instead.
export const CURRENT_SEASON = 2026;
export const SEASON_OPTIONS = [2026, 2025, 2024];

// Only real imported provider teams may ever render — never seed/legacy records.
export function isProviderTeam(t) {
  return !!t && (t.provider === "balldontlie" || t.data_state === "live");
}

export function normalizeTeam(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    abbr: raw.abbr || "??",
    name: raw.name || raw.abbr || "Unknown",
    location: raw.location || null,
    league: raw.league || raw.sport || null,
    conference: raw.conference ?? null,
    // Provider-resolved conference identity (kept so real names show once the
    // backend feed populates them; never dropped in favour of the numeric id).
    conference_name: raw.conference_name ?? null,
    conference_abbreviation: raw.conference_abbreviation ?? null,
    conference_id: raw.conference_id ?? null,
    division: raw.division ?? null,
    color: raw.color || null,
    record: raw.record || null,
    provider: raw.provider || null,
    data_state: raw.data_state || null,
    source_timestamp: raw.source_timestamp || raw.fetched_at || null,
  };
}

export function normalizeTeams(list) {
  return (Array.isArray(list) ? list : []).map(normalizeTeam).filter(Boolean);
}

export function teamsForLeague(list, league) {
  return normalizeTeams(list).filter(isProviderTeam).filter((t) => t.league === league);
}

// NFL → [{ conference, divisions:[{ division, teams:[] }] }]
export function groupNflTeams(teams) {
  return NFL_CONFERENCES.map((conf) => ({
    conference: conf,
    divisions: NFL_DIVISIONS.map((div) => ({
      division: div,
      teams: teams
        .filter((t) => String(t.conference).toUpperCase() === conf && String(t.division).toUpperCase() === div)
        .sort((a, b) => a.name.localeCompare(b.name)),
    })).filter((d) => d.teams.length),
  })).filter((c) => c.divisions.length);
}

export function ncaaConferences(teams) {
  const ids = [...new Set(teams.map((t) => t.conference).filter((v) => v !== null && v !== undefined))];
  return ids.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

// Honest label used when the provider has not resolved a conference's real
// name/abbreviation (stale numeric-only records). Never exposes the raw id.
export const NCAA_CONFERENCE_FALLBACK = "Conference (name unavailable)";

// Prefer the provider-resolved conference abbreviation/name for a conference id.
// NEVER renders the raw numeric provider id and never invents a mapping — if the
// feed hasn't resolved a name yet, returns an honest fallback. Pass the team list
// so the resolved name can be read from any team in that conference.
export function ncaaConferenceLabel(conf, teams) {
  const inConf = (Array.isArray(teams) ? teams : []).filter((t) => String(t.conference) === String(conf));
  for (const t of inConf) {
    const label = t.conference_abbreviation || t.conference_name;
    if (label && String(label).trim()) return String(label).trim();
  }
  return NCAA_CONFERENCE_FALLBACK;
}

export function teamsByNcaaConference(teams, conf) {
  return teams
    .filter((t) => String(t.conference) === String(conf))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function formatRecord(r) {
  if (!r) return null;
  const w = r.w ?? r.wins ?? 0;
  const l = r.l ?? r.losses ?? 0;
  const t = r.t ?? r.ties;
  return t ? `${w}-${l}-${t}` : `${w}-${l}`;
}

// ── Real data derived from the shared /games feed (no dedicated endpoint) ────
// A game's home/away carry `team_id` (same `team:<hash>` scheme as /teams), so a
// team's real schedule/results + record are derivable from the games feed.
export function teamGamesFromFeed(games, teamId) {
  const list = (Array.isArray(games) ? games : []).filter(
    (g) => g?.home?.team_id === teamId || g?.away?.team_id === teamId
  );
  return list
    .map((g) => {
      const isHome = g.home?.team_id === teamId;
      const self = isHome ? g.home : g.away;
      const opp = isHome ? g.away : g.home;
      const teamScore = self?.score ?? null;
      const oppScore = opp?.score ?? null;
      let result = null;
      if (g.status === "final" && teamScore != null && oppScore != null) {
        result = teamScore > oppScore ? "W" : teamScore < oppScore ? "L" : "T";
      }
      return {
        id: g.id,
        status: g.status,
        start_time: g.start_time || null,
        week: g.week ?? null,
        isHome,
        opponent: opp?.name || opp?.abbr || "—",
        opponentAbbr: opp?.abbr || null,
        teamScore,
        oppScore,
        result,
      };
    })
    .sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")));
}

export function computeRecordFromGames(teamGames) {
  const r = { w: 0, l: 0, t: 0 };
  for (const g of teamGames) {
    if (g.result === "W") r.w += 1;
    else if (g.result === "L") r.l += 1;
    else if (g.result === "T") r.t += 1;
  }
  return r;
}

export function normalizePlay(raw, i = 0) {
  if (!raw) return null;
  return {
    id: raw.id || raw.play_id || raw.sequence || i,
    sequence: raw.sequence ?? null,
    clock: raw.clock || raw.time || null,
    period: raw.period ?? raw.quarter ?? null,
    text: raw.text || raw.description || raw.detail || raw.summary || "",
    scoring: !!(raw.scoring_play ?? raw.scoring),
    team: raw.team_abbr || raw.team || null,
    awayScore: raw.away_score ?? raw.score_away ?? null,
    homeScore: raw.home_score ?? raw.score_home ?? null,
  };
}

// Canonical /sports/teams payload is pre-grouped: NFL by conference→divisions,
// NCAA by conference (no divisions). Normalize both into a uniform structure.
export function normalizeTeamsPayload(payload) {
  const confs = (payload?.conferences || []).map((c) => {
    if (Array.isArray(c.divisions)) {
      return { conference: c.conference, divisions: c.divisions.map((d) => ({ division: d.division, teams: d.teams || [] })) };
    }
    return { conference: c.conference, divisions: [{ division: null, teams: c.teams || [] }] };
  });
  return {
    conferences: confs,
    grouping: payload?.grouping || null,
    stale: !!payload?.stale,
    unavailable_reason: payload?.unavailable_reason || null,
    last_updated: payload?.last_updated || null,
    count: payload?.count ?? confs.reduce((n, c) => n + c.divisions.reduce((m, d) => m + d.teams.length, 0), 0),
  };
}

// Boxscore team_stats is a list [{team_abbr, stats:{k:v}}]. Build comparison rows.
export function boxscoreRows(teamStats) {
  const teams = (Array.isArray(teamStats) ? teamStats : []).filter((t) => t && t.stats && typeof t.stats === "object");
  if (teams.length < 1) return { teams: [], rows: [] };
  const keys = [...new Set(teams.flatMap((t) => Object.keys(t.stats)))];
  const rows = keys.map((k) => ({ label: k.replace(/_/g, " "), values: teams.map((t) => t.stats[k] ?? "—") }));
  return { teams: teams.map((t) => t.team_abbr || t.abbr || "—"), rows };
}

// Backward-compat single-game embedded stats comparison (legacy /games.stats).
export function teamStatComparison(gameStats) {
  if (!gameStats || typeof gameStats !== "object") return [];
  if (Array.isArray(gameStats.stats)) {
    return gameStats.stats.map((s) => ({ label: s.label || s.name, away: s.away, home: s.home }));
  }
  const away = gameStats.away || {};
  const home = gameStats.home || {};
  const keys = [...new Set([...Object.keys(away), ...Object.keys(home)])];
  return keys.map((k) => ({ label: k.replace(/_/g, " "), away: away[k], home: home[k] }));
}

// Extract the stored per-character prediction (pick, confidence, reasoning) from
// a Character Show episode's lines. NO shared winner, NO client fallback — each
// character's own stored line drives its own card. Returns [] when no episode.
export const CHAR_SHOW_ORDER = ["alpha_moose", "bruce", "cow_pie"];
export function extractPredictions(episode) {
  if (!episode || !Array.isArray(episode.lines)) return [];
  const out = [];
  for (const speaker of CHAR_SHOW_ORDER) {
    const line = episode.lines.find((l) => l.speaker === speaker && (l.pick_overlay || l.pick || l.confidence != null || l.reasoning));
    if (!line) continue;
    const po = line.pick_overlay || {};
    out.push({
      speaker,
      pick: po.su_abbr || line.pick || null,
      ats: po.ats || null,
      ou: po.ou || null,
      confidence: line.confidence ?? po.confidence ?? null,
      reasoning: line.reasoning || line.text || null,
    });
  }
  return out;
}

// pre-v4 strategy versions are historical and must be labeled "Legacy Show".
export function isLegacyShow(strategyVersion) {
  if (!strategyVersion) return true;
  const m = String(strategyVersion).match(/v?(\d+)/i);
  return m ? Number(m[1]) < 4 : true;
}

// Canonical independent predictions from the /character-show mismatch (or a
// /predictions cards) payload. Rendered EXACTLY by author_id — the real 2–1
// split, stored confidence and reasoning are preserved verbatim. No shared
// winner is inferred, no value is modified.
export function canonicalPickCards(canonicalPicks) {
  if (!Array.isArray(canonicalPicks)) return [];
  return canonicalPicks.map((p) => ({
    key: p.author_id,
    speaker: p.author_id,
    name: p.speaker_name || p.author_id,
    pick: p.su_abbr ?? null,
    teamId: p.su_team_id ?? null,
    ats: null,
    ou: null,
    confidence: p.confidence ?? null,
    reasoning: p.reasoning ?? null,
    strategy: p.strategy ?? null,
    strategyVersion: p.strategy_version ?? null,
  }));
}

// Map the /games/{id}/predictions `characters` cards into the independent
// prediction-card view model, resolving each su_pick team id → abbr via the
// game's own teams. Values are preserved verbatim (no shared winner inferred).
export function predictionCardsFromCharacters(characters, game) {
  if (!Array.isArray(characters)) return [];
  const abbr = {};
  const a = game && game.away, h = game && game.home;
  if (a) abbr[a.team_id ?? a.id] = a.abbr;
  if (h) abbr[h.team_id ?? h.id] = h.abbr;
  return CHAR_SHOW_ORDER
    .map((sp) => characters.find((c) => (c.author_id ?? c.character_id) === sp))
    .filter(Boolean)
    .map((c) => ({
      key: c.author_id ?? c.character_id,
      speaker: c.author_id ?? c.character_id,
      pick: abbr[c.su_pick] ?? c.su_abbr ?? null,
      teamId: c.su_pick ?? null,
      ats: c.ats_pick ?? null,
      ou: c.ou_pick ?? null,
      confidence: c.confidence ?? null,
      reasoning: c.reasoning ?? null,
      strategy: c.strategy ?? null,
      strategyVersion: c.strategy_version ?? null,
    }));
}

// Truthful guard for whether an episode may render as the CURRENT show.
// Rejects (in order) on ANY of the four integrity failures so a stale/invalid
// episode is NEVER shown or played as the current pregame show:
//   • validation_failed  — episode.validation.ok === false
//   • snapshot_mismatch   — prediction/card snapshot doesn't match locked records
//   • legacy              — strategy_version < 4 or missing (pre-v4)
//   • matchup_mismatch    — episode.game_id ≠ this game
//   • season_stale        — episode/snapshot season ≠ this game's season (or 2026)
// Uses BOTH the route gameId and the game object (for the current season).
export function episodeSeason(episode, game) {
  const raw = (episode && (episode.season
    ?? (episode.matchup && episode.matchup.season)
    ?? (episode.snapshot && episode.snapshot.season)
    ?? (episode.source_snapshot && episode.source_snapshot.season))) ?? null;
  if (raw != null && Number.isFinite(Number(raw))) return Number(raw);
  const st = (episode && episode.matchup && episode.matchup.start_time) || (game && game.start_time) || null;
  const y = st ? new Date(st).getUTCFullYear() : NaN;
  return Number.isFinite(y) ? y : null;
}

export function episodeRenderGuard(episode, gameId, game = null) {
  if (!episode) return { renderable: false, reason: null };
  if (episode.validation && episode.validation.ok === false) return { renderable: false, reason: "validation_failed" };
  if (episode.prediction_snapshot_mismatch === true || episode.snapshot_valid === false || episode.snapshot_mismatch === true) return { renderable: false, reason: "snapshot_mismatch" };
  const version = episode.strategy_version ?? episode.version ?? null;
  if (isLegacyShow(version)) return { renderable: false, reason: "legacy" };
  const epGameId = episode.game_id ?? (episode.matchup && episode.matchup.game_id) ?? null;
  if (epGameId != null && gameId != null && epGameId !== gameId) return { renderable: false, reason: "matchup_mismatch" };
  const gameSeason = (game && game.season != null && Number.isFinite(Number(game.season))) ? Number(game.season) : CURRENT_SEASON;
  const epSeason = episodeSeason(episode, game);
  if (epSeason != null && epSeason !== gameSeason) return { renderable: false, reason: "season_stale" };
  return { renderable: true, reason: null };
}

// Normalize a /games/{id}/character-show response into a truthful view model.
// A prediction_snapshot_mismatch NEVER surfaces the stale episode (episode=null)
// and exposes the canonical picks so the UI can render them independently.
export function resolveCharacterShow(resp) {
  const status = (resp && resp.status) ?? null;
  const mismatch = status === "prediction_snapshot_mismatch";
  return {
    status,
    mismatch,
    reason: (resp && resp.reason) ?? null,
    episode: mismatch ? null : ((resp && resp.episode) ?? null),
    coverage: (resp && resp.coverage) ?? null,
    canonicalPicks: canonicalPickCards(resp && resp.canonical_picks),
  };
}

// ── Publish 26 production shapes ────────────────────────────────────────────
// /sports/teams → { conferences:[{ conference, divisions:[{ division, teams:[] }] }] }
// NCAA variant may be { conferences:[{ conference, teams:[] }] }. Flatten either
// into a single provider team list (this endpoint IS the provider source).
export function flattenTeamsPayload(payload) {
  const out = [];
  for (const c of payload?.conferences || []) {
    if (Array.isArray(c.divisions)) {
      for (const d of c.divisions) for (const t of d.teams || []) out.push(t);
    } else {
      for (const t of c.teams || []) out.push(t);
    }
  }
  if (!out.length && Array.isArray(payload?.teams)) return payload.teams;
  return out;
}

// /sports/standings → { groups:[{ conference, divisions:[{ division, standings:[] }] }] }
// Flatten to a uniform [{ conference, division, teams:[] }].
export function flattenStandingsGroups(groups) {
  const out = [];
  for (const g of groups || []) {
    if (Array.isArray(g.divisions)) {
      for (const d of g.divisions) out.push({ conference: g.conference, division: d.division, teams: d.standings || d.teams || [] });
    } else {
      out.push({ conference: g.conference, division: g.division || null, teams: g.standings || g.teams || [] });
    }
  }
  return out;
}

// /sports/team/{id}.schedule → [{ id, week, status, start_time, home:bool,
// opponent_abbr, team_score, opponent_score }]. Normalize to the same render
// shape as teamGamesFromFeed so TeamDetail can render either source uniformly.
export function normalizeContractSchedule(schedule) {
  return (Array.isArray(schedule) ? schedule : []).map((g) => {
    const teamScore = g.team_score ?? g.teamScore ?? null;
    const oppScore = g.opponent_score ?? g.oppScore ?? null;
    let result = null;
    if (g.status === "final" && teamScore != null && oppScore != null) {
      result = teamScore > oppScore ? "W" : teamScore < oppScore ? "L" : "T";
    }
    return {
      id: g.id,
      status: g.status,
      start_time: g.start_time || null,
      week: g.week ?? null,
      isHome: g.home ?? g.isHome ?? false,
      opponent: g.opponent || g.opponent_abbr || g.opponentAbbr || "—",
      opponentAbbr: g.opponent_abbr || g.opponentAbbr || null,
      teamScore,
      oppScore,
      result,
    };
  });
}



// ── Player pages (current season) ──────────────────────────────────────────
// Consumes GET /api/v1/sports/player/{id}?season=. Provider-neutral, verified
// data only. Never fabricates identity, stats, team, or roster completeness.

// Link target for a player, or null when no stable internal id exists (caller
// then renders plain text — see PlayerName).
export function playerHref(id) { return id ? `/player/${encodeURIComponent(id)}` : null; }

// Coverage → honest render state. supported=false → unavailable; stale flag →
// stale; otherwise ok. "partial" is derived at the page level from thin data.
export function playerCoverageState(coverage) {
  const c = coverage || {};
  if (c.supported === false) return "unavailable";
  if (coverageIsProcessing(c)) return "processing";
  if (c.stale === true || c.is_stale === true) return "stale";
  return "ok";
}

export function normalizePlayerGameLogEntry(e) {
  const x = e || {};
  const reserved = new Set(["game_id", "id", "date", "game_date", "opponent", "matchup", "opponent_abbr", "team", "team_abbr", "status", "game_status", "result", "stats", "stat_line"]);
  let stats = x.stats || x.stat_line || null;
  if (!stats) { stats = {}; for (const [k, v] of Object.entries(x)) if (!reserved.has(k)) stats[k] = v; }
  return {
    gameId: x.game_id || x.id || null,
    date: x.date || x.game_date || null,
    opponent: x.opponent || x.matchup || x.opponent_abbr || null,
    team: x.team || x.team_abbr || null,
    status: x.status || x.game_status || null,
    result: x.result || null,
    stats,
  };
}

// Normalize the canonical player payload into a stable view model. Returns
// { state, player, season, seasonStats, gameLog, coverage }.
export function normalizePlayerResponse(raw, requestedSeason) {
  const d = raw || {};
  const p = d.player || {};
  const coverage = d.coverage || null;
  const state = playerCoverageState(coverage);
  const team = p.team || {};
  const player = {
    id: p.id || p.player_id || null,
    name: p.display_name || p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || null,
    firstName: p.first_name || null,
    lastName: p.last_name || null,
    league: p.league || null,
    teamId: p.team_id || team.id || null,
    teamName: p.team_name || team.name || null,
    teamAbbr: p.team_abbr || team.abbr || null,
    position: p.position || null,
    jersey: p.jersey ?? p.jersey_number ?? null,
    height: p.height || null,
    weight: p.weight || null,
    college: p.college || null,
    experience: p.experience ?? p.experience_years ?? null,
    status: p.status || null,
  };
  const seasonStats = d.season_stats && typeof d.season_stats === "object" && Object.keys(d.season_stats).length ? d.season_stats : null;
  const gameLog = Array.isArray(d.game_log) ? d.game_log.map(normalizePlayerGameLogEntry) : [];
  return { state, player, season: d.season ?? requestedSeason ?? null, seasonStats, gameLog, coverage, retryAfter: readRetryAfter(d) };
}

// ── Players directory (current season) ──────────────────────────────────────
// Consumes GET /api/v1/sports/players. Verified rows only; a row without a
// stable id is kept but is NOT linkable (see PlayerName / directory card).
export function normalizePlayerListItem(p) {
  const x = p || {};
  return {
    id: x.id || x.player_id || null,
    name: x.name || x.display_name || `${x.first_name || ""} ${x.last_name || ""}`.trim() || null,
    firstName: x.first_name || null,
    lastName: x.last_name || null,
    league: x.league || null,
    teamId: x.team_id || null,
    teamName: x.team_name || null,
    teamAbbr: x.team_abbr || null,
    position: x.position || null,
    jersey: x.jersey ?? x.jersey_number ?? null,
    headshotUrl: x.headshot_url || null,
  };
}

export function normalizePlayersResponse(raw) {
  const d = raw || {};
  return {
    state: d.state || "ok",
    players: Array.isArray(d.players) ? d.players.map(normalizePlayerListItem) : [],
    nextCursor: d.next_cursor || null,
    season: d.season ?? null,
    coverage: d.coverage || null,
  };
}
